import { FILTER_MAP } from '../constants.js';
import { matchSession } from './search.js';

/**
 * Get the available filters based on filterOptions and embedFields
 * This is a unified function that works with both Sessions and Schedule widgets
 *
 * @param {import('../types/async-data.js').SessionFilterOptions} filterOptions - The filter options object with boolean flags
 * @param {import('../types/async-data.js').EmbedField[]} embedFields - The embed fields array with filter configurations
 * @returns {Object} The available filters object with structure:
 *   {
 *     [internalName]: {
 *       name: string,
 *       options: Array<{id, label}>,
 *       allowMultiple: boolean
 *     }
 *   }
 */
export const getAvailableFilters = (filterOptions, embedFields) => {
  if (!filterOptions || !embedFields) {
    return {};
  }

  const filters = {};

  // Build available filters using FILTER_MAP from constants
  Object.entries(FILTER_MAP).forEach(([filterKey, internalName]) => {
    // Check if this filter is enabled in filterOptions
    if (!filterOptions[filterKey]) return;

    // Find the corresponding embed field
    const embedField = embedFields.find(field => field.internal_name === internalName);
    if (
      !embedField ||
      !embedField.settings ||
      !embedField.settings.options ||
      embedField.settings.options.length === 0
    ) {
      return; // Skip if no options available
    }

    filters[internalName] = {
      name: embedField.name,
      options: embedField.settings.options,
      allowMultiple: embedField.settings.allowMultipleSelect || false,
    };
  });

  return filters;
};

/** Match tag filters by tagIds (always present) with a labels fallback. */
const sessionMatchesTagValues = (session, values) => {
  if (Array.isArray(session.tagIds)) {
    return session.tagIds.some(id => values.includes(id));
  }
  return Boolean(session.tags?.some(tag => values.includes(tag.id)));
};

/**
 * Advanced filtering function that implements sophisticated OR/AND filter logic
 * This function matches the legacy system's behavior and is extracted from SessionsWidget
 *
 * @param {Array} sessions - Array of sessions to filter
 * @param {string} searchQuery - Search query string
 * @param {Object} activeFilters - Object with filter type as key and selected values as array
 * @param {Object} availableFilters - Available filter configurations with allowMultiple settings
 * @param {Object} options - Configuration options for different widgets
 * @param {Array} options.searchFields - Fields to search in ['title', 'description', 'speakers']
 * @param {string} options.fieldMappingStyle - 'id' for formatId/trackId (default; used by Sessions and Schedule). 'direct' matches labels.
 * @returns {Array} Filtered sessions array
 */
export const advancedFilterSessions = (
  sessions = [],
  searchQuery = '',
  activeFilters = {},
  availableFilters = {},
  options = {}
) => {
  const {
    searchFields = ['title', 'speakers'], // Default search fields
    fieldMappingStyle = 'id', // Sessions + Schedule always have formatId/trackId/…
  } = options;

  let filtered = [...sessions];

  // Apply search filter first (matches legacy behavior)
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(session => matchSession(session, query, searchFields));
  }

  // Separate filters into OR (multi-select) and AND (single-select) groups
  const orFilters = []; // Multi-select filters (like tags)
  const andFilters = []; // Single-select filters (like format, track)

  Object.entries(activeFilters).forEach(([filterType, selectedValues]) => {
    if (selectedValues.length > 0) {
      const filterConfig = availableFilters[filterType];
      if (filterConfig?.allowMultiple) {
        // OR logic for multi-select filters
        orFilters.push({ type: filterType, values: selectedValues });
      } else {
        // AND logic for single-select filters
        andFilters.push({ type: filterType, values: selectedValues });
      }
    }
  });

  // Apply OR filters (multi-select) - session must match ANY of the selected values within each OR filter
  if (orFilters.length > 0) {
    filtered = filtered.filter(session =>
      orFilters.some(filter => {
        if (filter.type === 'tags') {
          return sessionMatchesTagValues(session, filter.values);
        }

        // Handle other OR filters based on field mapping style
        if (fieldMappingStyle === 'id') {
          // Sessions props style like: formatId, trackId, etc.
          return filter.values.some(value => session[`${filter.type}Id`] === value);
        } else {
          // ScheduleWidget style: format, track, etc. - need to lookup ID from label
          const fieldValue = session[filter.type];
          if (!fieldValue) return false;

          // Find the corresponding option ID from available filters
          const filterConfig = availableFilters[filter.type];
          const option = filterConfig?.options?.find(opt => opt.label === fieldValue);
          return option && filter.values.includes(option.id);
        }
      })
    );
  }

  // Apply AND filters (single-select) - session must match ALL selected filters
  if (andFilters.length > 0) {
    filtered = filtered.filter(session =>
      andFilters.every(filter => {
        if (filter.type === 'tags') {
          return sessionMatchesTagValues(session, filter.values);
        }

        // Handle other AND filters based on field mapping style
        if (fieldMappingStyle === 'id') {
          // SessionsWidget style: formatId, trackId, etc.
          return filter.values.includes(session[`${filter.type}Id`]);
        } else {
          // ScheduleWidget style: format, track, etc. - need to lookup ID from label
          const fieldValue = session[filter.type];
          if (!fieldValue) return false;

          // Find the corresponding option ID from available filters
          const filterConfig = availableFilters[filter.type];
          const option = filterConfig?.options?.find(opt => opt.label === fieldValue);
          return option && filter.values.includes(option.id);
        }
      })
    );
  }

  return filtered;
};
